import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import confetti from 'canvas-confetti';
import { useScoring } from '../../../../shared';
import { useAppContext } from '@coverright/shared/contexts';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Award, ArrowRight, Pill, ShieldCheck, Sparkles, Target, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';

// ─── Types ────────────────────────────────────────────────────────────────────

type CelebrationScore = 70 | 95;

// ─── Confetti ─────────────────────────────────────────────────────────────────

const COLORS = ['#009689', '#34d399', '#9333ea', '#d8b4fe', '#f59e0b'];

function fireConfetti() {
  const end = Date.now() + 3000;

  (function frame() {
    confetti({ particleCount: 5, angle: 60,  spread: 55, origin: { x: 0 }, colors: COLORS });
    confetti({ particleCount: 5, angle: 120, spread: 55, origin: { x: 1 }, colors: COLORS });
    if (Date.now() < end) requestAnimationFrame(frame);
  }());
}

// ─── Modal 70% ────────────────────────────────────────────────────────────────

function Modal70({ open, onClose }: { open: boolean; onClose: () => void }) {
  const location = useLocation();
  const isAdvanced = location.pathname.includes('plans-advanced');
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 1.5, overflow: 'hidden' } }}>
      <Box sx={{ height: 8, background: 'linear-gradient(to right, #009689, #34d399)' }} />
      <IconButton onClick={onClose} size="small"
        sx={{ position: 'absolute', borderRadius: 999, top: 16, right: 16, bgcolor: '#f3f4f6', '&:hover': { bgcolor: '#e5e7eb' } }}>
        <X size={16} color="#6b7280" />
      </IconButton>
      <DialogContent sx={{ p: 4, textAlign: 'center' }}>
        <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: '#e6f4f2', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
          <Award size={32} color="#009689" />
        </Box>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#111827', mb: 1 }}>
          You're on the right track!
        </Typography>
        <Typography sx={{ fontSize: 15, color: '#4b5563', mb: 3, lineHeight: 1.6 }}>
          You've reached a <strong>70% Match Score</strong>! However, to unlock your{' '}
          <strong>AI-personalized top 3</strong> plan recommendations, we need a bit more detail.{' '}
          {isAdvanced && <>
            Look for the{' '}
            <Box component="span" sx={{
              display: 'inline-flex', alignItems: 'center', gap: '2px', mx: '2px',
              background: 'linear-gradient(to right, #009689, #34d399)',
              color: '#fff', fontSize: 10, fontWeight: 700,
              px: '6px', py: '2px', borderRadius: 999, verticalAlign: 'middle',
            }}>
              <Award size={10} /> A.I. Ranked
            </Box>{' '}
            badge on your top choices.
          </>}

        </Typography>

        <Box sx={{ bgcolor: '#f9fafb', borderRadius: 3, p: 2, mb: 3, border: '1px solid #f3f4f6', textAlign: 'left' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5 }}>
            Activities to unlock your recommendation:
          </Typography>
          <Stack gap={1.5}>
            <Stack direction="row" alignItems="flex-start" gap={1}>
              <Pill size={16} color="#5472e8" style={{ marginTop: 2, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 14, color: '#4b5563' }}>
                <strong>Add your prescriptions</strong> to check covered costs.
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="flex-start" gap={1}>
              <ShieldCheck size={16} color="#9333ea" style={{ marginTop: 2, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 14, color: '#4b5563' }}>
                <strong>Add other coverage</strong> to avoid conflicts.
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="flex-start" gap={1}>
              <Target size={16} color="#e11d48" style={{ marginTop: 2, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 14, color: '#4b5563' }}>
                <strong>Set your priorities</strong> to tailor the options.
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Button
          fullWidth
          variant="contained"
          size={'small'}
          data-test="celebration-continue"
          endIcon={<ArrowRight size={16} />}
          onClick={onClose}
          sx={{ bgcolor: '#009689', '&:hover': { bgcolor: '#008276' }, borderRadius: 1.5, py: 1.5, fontWeight: 700, fontSize: 15, textTransform: 'none', boxShadow: '0 4px 12px rgba(0,150,137,0.3)' }}
        >
          Continue
        </Button>
      </DialogContent>
    </Dialog>
  );
}

// ─── Modal 95% ────────────────────────────────────────────────────────────────

function Modal95({ open, onClose }: { open: boolean; onClose: () => void }) {
  const location = useLocation();
  const isAdvanced = location.pathname.includes('plans-advanced');
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 1.5, overflow: 'hidden' } }}>
      <Box sx={{ height: 8, background: 'linear-gradient(to right, #9333ea, #d8b4fe)' }} />
      <IconButton onClick={onClose} size="small"
        sx={{ position: 'absolute', top: 16, right: 16, bgcolor: '#f3f4f6', '&:hover': { bgcolor: '#e5e7eb' } }}>
        <X size={16} color="#6b7280" />
      </IconButton>
      <DialogContent sx={{ p: 4, textAlign: 'center' }}>
        <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: '#faf5ff', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
          <Sparkles size={32} color="#9333ea" />
        </Box>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#111827', mb: 1 }}>
          Excellent Match Score!
        </Typography>
        <Typography sx={{ fontSize: 15, color: '#4b5563', mb: 3, lineHeight: 1.6 }}>
          You've reached a <strong>95% Match Score</strong>! We now have enough information to provide
          your <strong>AI-personalized top 3</strong> plan recommendations.{' '}
          {isAdvanced && <>
            Look for the{' '}
            <Box component="span" sx={{
              display: 'inline-flex', alignItems: 'center', gap: '2px', mx: '2px',
              background: 'linear-gradient(to right, #8200db, #b829ff)',
              color: '#fff', fontSize: 10, fontWeight: 700,
              px: '6px', py: '2px', borderRadius: 999, verticalAlign: 'middle',
            }}>
              <Sparkles size={10} /> Recommended
            </Box>{' '}
            badge on your top choices.
          </>}
        </Typography>

        <Button
          fullWidth
          size={'small'}
          variant="contained"
          endIcon={<ArrowRight size={16} />}
          onClick={onClose}
          sx={{ bgcolor: '#9333ea', '&:hover': { bgcolor: '#7e22ce' }, borderRadius: 1.5, py: 1.5, fontWeight: 700, fontSize: 15, textTransform: 'none', boxShadow: '0 4px 12px rgba(147,51,234,0.3)' }}
        >
          View Recommended Plans
        </Button>
      </DialogContent>
    </Dialog>
  );
}

// ─── Celebration node (portals for both modals) ───────────────────────────────

function CelebrationModals({ open, onClose }: { open: CelebrationScore | null; onClose: () => void }) {
  return (
    <>
      <Modal70 open={open === 70} onClose={onClose} />
      <Modal95 open={open === 95} onClose={onClose} />
    </>
  );
}

// ─── Auto-trigger hook ────────────────────────────────────────────────────────

const STORAGE_KEY_70 = 'cr_celebration_70_shown';
const STORAGE_KEY_95 = 'cr_celebration_95_shown';

export function useCelebrationAutoTrigger() {
  const { matchScore, aiGateOpen } = useScoring();
  const [{ planType }] = useAppContext();
  const [open, setOpen] = useState<CelebrationScore | null>(null);
  const prevScore = useRef(matchScore);

  useEffect(() => {
    const prev = prevScore.current;
    prevScore.current = matchScore;

    // Only trigger when score increases (not on initial load from 0)
    if (matchScore <= prev || prev === 0) return;

    if (matchScore >= 95 && aiGateOpen && !!planType && !sessionStorage.getItem(STORAGE_KEY_95)) {
      sessionStorage.setItem(STORAGE_KEY_95, '1');
      fireConfetti();
      setOpen(95);
    } else if (matchScore >= 70 && !sessionStorage.getItem(STORAGE_KEY_70)) {
      sessionStorage.setItem(STORAGE_KEY_70, '1');
      fireConfetti();
      setOpen(70);
    }
  }, [matchScore, aiGateOpen, planType]);

  return <CelebrationModals open={open} onClose={() => setOpen(null)} />;
}
